import { useState, useEffect, useRef, useCallback } from "react";
import { useApi } from "./useApi";
import { useModuleAvailability } from "./useModuleAvailability";

const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export const useDarkWebBadge = (orgId) => {
  const [count, setCount] = useState(0);
  const lastFetchRef = useRef(0);
  const intervalRef = useRef(null);
  const apiCall = useApi();
  // No polling while the feature flags load, nor when the deployment profile cannot run dark-web
  // monitoring (egress catalog module `darkWebMonitoring`): the menu item is hidden there too.
  const { loaded, isModuleAvailable } = useModuleAvailability();
  const enabled = loaded && isModuleAvailable("darkWebMonitoring");

  const fetchCount = useCallback(async (force = false) => {
    if (!orgId || !enabled) {
      setCount(0);
      return;
    }

    const now = Date.now();
    if (!force && now - lastFetchRef.current < CACHE_DURATION_MS) {
      return;
    }

    try {
      // suppressAuthRedirect=true: background badge poll — a 401/403 must not bounce the app
      // through OIDC / the org picker. Fail quietly (the catch below ignores it).
      const data = await apiCall("GET", "/enzoic/new-exposure-count", null, false, null, true);
      setCount(data?.count ?? 0);
      lastFetchRef.current = Date.now();
    } catch (err) {}
  }, [orgId, enabled, apiCall]);

  const resetCount = useCallback(async () => {
    setCount(0);
    try {
      await apiCall("POST", "/enzoic/mark-monitoring-viewed");
      lastFetchRef.current = Date.now();
    } catch (err) {}
  }, [apiCall]);

  // Fetch on mount and when orgId (or availability) changes
  useEffect(() => {
    if (!orgId || !enabled) {
      setCount(0);
      return;
    }

    // Reset cache on org change to force immediate fetch
    lastFetchRef.current = 0;
    fetchCount(true);

    // Set up polling interval
    intervalRef.current = setInterval(() => fetchCount(true), CACHE_DURATION_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [orgId, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return { count, resetCount, refetch: fetchCount };
};
